import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiErrorMessage } from '../../../../types/api-errors';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { useBusiness } from '../../../../hooks/useBusiness';
import type { UpdateBusinessRequest } from '../../../../services/api';
import { apiService } from '../../../../services/api';
import { ServiceType } from '../../../../types/enums';
import Button from '../../../../components/Button/Button';
import InputField from '../../../../components/InputField/InputField';
import TextArea from '../../../../components/TextArea/TextArea';
import Toggle from '../../../../components/Toggle/Toggle';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';

import 'leaflet/dist/leaflet.css';
import styles from './AccountSettings.module.css';

// O3 (SO-38) — English day names stay the internal keys (state / payload mapping);
// only the DISPLAY goes through t().
const DAY_LABEL_KEYS: Record<string, string> = {
    Sunday: 'ost.day.0', Monday: 'ost.day.1', Tuesday: 'ost.day.2', Wednesday: 'ost.day.3',
    Thursday: 'ost.day.4', Friday: 'ost.day.5', Saturday: 'ost.day.6',
};

const AccountSettings: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const dayLabel = (day: string): string => (DAY_LABEL_KEYS[day] ? t(DAY_LABEL_KEYS[day]) : day);
    /** Display label for an hours option ('24 hours', '9:00 AM', 'Closed'); the value stays English. */
    const hourOptionLabel = (option: string): string => {
        if (option === '24 hours') return t('oset.hours.allDay');
        if (option === 'Closed') return t('oset.hours.closed');
        return option
            .replace(/\s*AM$/i, ` ${t('oset.hours.am')}`)
            .replace(/\s*PM$/i, ` ${t('oset.hours.pm')}`);
    };
    const { checkBusinessExists, updateBusiness, deleteBusiness } = useBusiness();
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Persist identifiers and non-edit fields
    const [businessId, setBusinessId] = useState<number | null>(null);
    const [categoryIds, setCategoryIds] = useState<number[]>([]);
    const [serviceType, setServiceType] = useState<number>(1);
    const [, setBusinessRegistrationNumber] = useState<string>('');

    const handleClick = useCallback(() => {
        navigate('/settings');
    }, [navigate]);

    const [formData, setFormData] = useState({
        businessName: '',
        about: '',
        category: '',
        alwaysOpen: false,
        location: {
            country: '',
            state: '',
            streetAddress: '',
            city: '',
            zipCode: '',
            homeServicesAvailable: false,
            latitude: 25.276987,
            longitude: 55.296249
        }
    });

    const [weekDays, setWeekDays] = useState<string[]>(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday']);
    const [businessHours, setBusinessHours] = useState<{ [key: string]: { from: string, to: string } }>({
        Sunday: { from: '24 hours', to: '24 hours' },
        Monday: { from: '9:00 AM', to: '10:00PM' },
        Tuesday: { from: '9:00 AM', to: '10:00PM' },
        Wednesday: { from: '9:00 AM', to: '10:00PM' },
        Thursday: { from: '9:00 AM', to: '10:00PM' },
        Friday: { from: 'Closed', to: 'Closed' },
        Saturday: { from: 'Closed', to: 'Closed' }
    });

    const getDayName = useCallback((dayNumber: number): string => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[dayNumber] || days[0];
    }, []);

    const loadBusinessData = useCallback(async () => {
        setIsLoading(true);
        try {
            const business = await checkBusinessExists();
            if (business) {
                // Persist IDs and metadata
                setBusinessId(business.id);
                setCategoryIds((business.categories as Array<{ categoryId?: number }> | undefined)?.map(c => c.categoryId).filter((id): id is number => id != null && Number.isFinite(id)) ?? []);
                // serviceType may come as string; try parse number, fallback to 1
                const parsedServiceType = typeof business.serviceType === 'string' ? parseInt(business.serviceType as any, 10) : (business.serviceType as any);
                setServiceType(Number.isFinite(parsedServiceType) ? parsedServiceType : 1);
                setBusinessRegistrationNumber(business.businessRegistrationNumber || '');

                setFormData({
                    businessName: business.name,
                    about: business.about,
                    category: (business.categories as Array<{ name?: string }>)?.[0]?.name ?? '',
                    alwaysOpen: business.isAlwaysOpen,
                    location: (() => {
                        const loc = business.location as Record<string, unknown>;
                        return {
                            country: (loc.country as string) ?? '',
                            state: (loc.state as string) ?? '',
                            streetAddress: (loc.streetAddress as string) ?? '',
                            city: (loc.city as string) ?? '',
                            zipCode: (loc.zipCode as string) ?? '',
                            homeServicesAvailable: Boolean(loc.hasHomeService),
                            latitude: typeof loc.latitude === 'number' ? loc.latitude : 0,
                            longitude: typeof loc.longitude === 'number' ? loc.longitude : 0
                        };
                    })()
                });

                const formattedHours: { [key: string]: { from: string, to: string } } = {};
                (business.businessHours as Array<{ dayOfWeek: number | string; is24Hours?: boolean; openTime?: string; closeTime?: string; isOpen?: boolean }>).forEach(hour => {
                    let day: string;
                    if (typeof hour.dayOfWeek === 'string') {
                        const parsed = parseInt(hour.dayOfWeek, 10);
                        if (!isNaN(parsed)) {
                            day = getDayName(parsed);
                        } else {
                            day = hour.dayOfWeek;
                        }
                    } else {
                        day = getDayName(hour.dayOfWeek);
                    }
                    formattedHours[day] = {
                        from: hour.is24Hours ? '24 hours' : (hour.openTime as string) || 'Closed',
                        to: hour.is24Hours ? '24 hours' : (hour.closeTime as string) || 'Closed'
                    };
                });
                setBusinessHours(formattedHours);

                const hours = business.businessHours as Array<{ dayOfWeek: number | string; isOpen?: boolean }>;
                setWeekDays(hours
                    .filter(hour => hour.isOpen)
                    .map(hour => {
                        if (typeof hour.dayOfWeek === 'string') {
                            const parsed = parseInt(hour.dayOfWeek, 10);
                            if (!isNaN(parsed)) {
                                return getDayName(parsed);
                            }
                            return hour.dayOfWeek;
                        }
                        return getDayName(hour.dayOfWeek);
                    })
                );
            }
        } catch (error) {
            console.error('Error loading business data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [checkBusinessExists, getDayName]);

    useEffect(() => {
        loadBusinessData();
    }, [loadBusinessData]);

    const dayHoursList = [
        '24 hours',
        '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM', '5:00 AM',
        '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
        '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
        '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM'
    ];

    // #1211: XSS sanitization for text inputs
    const sanitizeInput = (value: string): string => value.replace(/[<>&]/g, '');

    const handleInputChange = useCallback((name: string, value: string) => {
        // #1211: sanitize business name to prevent XSS
        const sanitized = name === 'businessName' ? sanitizeInput(value) : value;
        setFormData(prevData => ({ ...prevData, [name]: sanitized }));
    }, []);

    const toggleAlwaysOpen = useCallback(() => {
        setFormData(prevData => ({ ...prevData, alwaysOpen: !prevData.alwaysOpen }));
    }, []);

    const handleLocationChange = useCallback((name: string, value: string | number | boolean) => {
        setFormData(prevData => ({
            ...prevData,
            location: { ...prevData.location, [name]: value }
        }));
    }, []);

    const handleWeekDayChange = useCallback((day: string) => {
        setWeekDays(prevState => {
            if (prevState.includes(day)) {
                const newState = prevState.filter(d => d !== day);
                setBusinessHours(prev => {
                    const { [day]: removed, ...rest } = prev;
                    return rest;
                });
                return newState;
            }
            // When enabling a day, default its hours to 24 hours
            setBusinessHours(prev => ({
                ...prev,
                [day]: { from: '24 hours', to: '24 hours' }
            }));
            return [...prevState, day];
        });
    }, []);

    const handleBusinessHourChange = useCallback((day: string, time: 'from' | 'to', value: string | string[]) => {
        setBusinessHours(prevState => ({
            ...prevState,
            [day]: {
                ...prevState[day],
                [time]: value,
            },
        }));
    }, []);

    const weekDaysList = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Helpers
    const to24Hour = (time?: string): string | undefined => {
        if (!time) return undefined;
        const t = time.trim();
        if (t.toLowerCase() === '24 hours' || t.toLowerCase() === 'closed') return undefined;
        // Expect format like '9:00 AM' or '10:00 PM'
        const match = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) return undefined;
        const hh = match[1];
        const mm = match[2];
        const mer = match[3];
        let h = parseInt(hh, 10);
        const isPM = mer.toUpperCase() === 'PM';
        if (h === 12) h = isPM ? 12 : 0; else if (isPM) h += 12;
        const hh24 = h.toString().padStart(2, '0');
        return `${hh24}:${mm}`;
    };

    const toMinutes = (time?: string): number | undefined => {
        const t24 = to24Hour(time);
        if (!t24) return undefined;
        const [hh, mm] = t24.split(':').map(Number);
        if (Number.isNaN(hh) || Number.isNaN(mm)) return undefined;
        return hh * 60 + mm;
    };

    const allWeekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // #1212: Only show marker when at least one address field is filled
    const hasAddressData = useMemo(() => {
        const loc = formData.location;
        return !!(loc.country || loc.state || loc.city || loc.streetAddress || loc.zipCode);
    }, [formData.location]);

    const handleSave = async () => {
        if (!businessId) {
            showToast(t('oset.account.noBusiness'), 'error');
            return;
        }
        // Validations
        if (!formData.businessName || formData.businessName.trim().length === 0) {
            showToast(t('oset.account.nameRequired'), 'warning');
            return;
        }
        for (const dayName of allWeekDays) {
            const isOpen = weekDays.includes(dayName);
            if (!isOpen) continue;
            const entry = businessHours[dayName] || { from: '', to: '' };
            const is24 = formData.alwaysOpen || entry.from === '24 hours' || entry.to === '24 hours';
            if (is24) continue;
            const startMin = toMinutes(entry.from);
            const endMin = toMinutes(entry.to);
            if (startMin == null || endMin == null) {
                showToast(t('oset.account.invalidHours').replace('{day}', dayLabel(dayName)), 'warning');
                return;
            }
            if (endMin <= startMin) {
                showToast(t('oset.account.closeAfterOpen').replace('{day}', dayLabel(dayName)), 'warning');
                return;
            }
        }
        setIsSaving(true);
        try {
            // Build BusinessHours array for all 7 days
            const hours = allWeekDays.map((dayName, idx) => {
                const isOpen = weekDays.includes(dayName);
                const entry = businessHours[dayName] || { from: '', to: '' };
                const is24 = formData.alwaysOpen || entry.from === '24 hours' || entry.to === '24 hours';
                return {
                    dayOfWeek: idx,
                    isOpen,
                    openTime: isOpen && !is24 ? to24Hour(entry.from) : undefined,
                    closeTime: isOpen && !is24 ? to24Hour(entry.to) : undefined,
                    is24Hours: isOpen && is24
                };
            });

            // #1209: Backend expects a FLAT payload (UpdateBusinessRequest) with location
            // fields at the top level — NOT nested under a `location` key. Previously the
            // payload used `as any` to bypass this mismatch, so location edits were
            // silently dropped server-side.
            const payload: UpdateBusinessRequest = {
                name: formData.businessName,
                about: formData.about,
                isAlwaysOpen: formData.alwaysOpen,
                serviceType: serviceType as ServiceType,
                country: formData.location.country,
                state: formData.location.state,
                streetAddress: formData.location.streetAddress,
                city: formData.location.city,
                zipCode: formData.location.zipCode,
                hasHomeService: formData.location.homeServicesAvailable,
                latitude: formData.location.latitude,
                longitude: formData.location.longitude,
                businessHours: hours,
                categoryIds: categoryIds
            };

            await updateBusiness(businessId, payload);
            showToast(t('oset.account.saved'), 'success');
            // Reload to reflect any canonical formatting from server
            await loadBusinessData();
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, t('oset.account.saveFailed')); // AB#1919 (WS-5b)
            showToast(msg, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteBusiness = async () => {
        if (!businessId) return;
        setIsDeleting(true);
        setShowDeleteModal(false);
        try {
            await deleteBusiness(businessId);
            showToast(t('oset.account.deleted'), 'success');
            apiService.clearTokens();
            navigate('/form/login');
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, t('oset.account.deleteFailed')); // AB#1919 (WS-5b)
            showToast(msg, 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={styles.accountSettings} aria-busy={isLoading || isSaving || isDeleting}>
            {(isLoading || isSaving) && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.spinner}>
                        <svg viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                        </svg>
                        <span>{isSaving ? t('staff.saving') : t('oset.loading')}</span>
                    </div>
                </div>
            )}
            <div className={styles.headerBckBTN}>
                {/* O3 (SO-44) — the back control is a real button (was a clickable svg). */}
                <button
                    type="button"
                    onClick={handleClick}
                    className={styles.backButton}
                    aria-label={t('oset.backToSettings')}
                >
                    <svg aria-hidden="true" focusable="false" width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect style={{ fill: 'var(--primary-subtle)' }} width="38" height="38" rx="14" />
                        <path style={{ stroke: 'var(--primary)' }} d="M23 12L16 19L23 26" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <h1 className='xH1'>{t('settings.card.accountSettings.title')}</h1>
                <div style={{ marginInlineStart: 'auto' }}>
                    <Button
                        label={isSaving ? t('staff.saving') : t('oset.saveChanges')}
                        variant="primary"
                        disabled={isSaving || isLoading || !businessId}
                        onClick={handleSave}
                    />
                </div>
            </div>

            {/* Profile Section */}
            {/* #1210: Show transparent placeholder instead of misleading default images for new accounts */}
            <div className={styles.profileSection}>
                <div className={styles.coverImage} style={{ backgroundColor: 'var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                    {formData.businessName ? null : t('oset.account.coverPhoto')}
                </div>
                <div className={styles.logo} style={{ backgroundColor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', color: 'var(--text-tertiary)', fontSize: '0.7rem', textAlign: 'center' as const }}>
                    {formData.businessName ? formData.businessName.charAt(0).toUpperCase() : t('oset.account.logo')}
                </div>
                <div className={styles.businessInfo}>
                    <InputField
                        label={t('oset.account.businessName')}
                        value={formData.businessName}
                        onChange={(value) => handleInputChange('businessName', value)}
                    />
                    {/* #1212: Add maxLength to About field */}
                    <TextArea
                        label={t('oset.account.about')}
                        value={formData.about}
                        onChange={(value) => handleInputChange('about', value)}
                        maxLength={500}
                    />
                    {/* #1212: Visible character counter for About field */}
                    <span
                        className={styles.charCounter}
                        style={{
                            color: (formData.about?.length ?? 0) > 450 ? 'var(--destructive-fg)' : 'var(--text-tertiary)',
                        }}
                    >
                        {t('oset.account.charCount').replace('{count}', String(formData.about?.length ?? 0)).replace('{max}', '500')}
                    </span>

                </div>
            </div>



            {/* Business Hours Section */}
            <div className={styles.businessHoursSection}>
                <h2 className={styles.h2}>{t('oset.account.businessHours')}</h2>
                <Toggle
                    label={t('oset.account.alwaysOpen')}
                    checked={formData.alwaysOpen}
                    onChange={toggleAlwaysOpen}
                />
                {weekDaysList.map(day => (
                    <div key={day} className={styles.weekDayRow}>
                        <Checkbox
                            label={dayLabel(day)}
                            checked={weekDays.includes(day)}
                            onChange={() => handleWeekDayChange(day)}
                        />
                        <div className={styles.dropdownContainer}>
                            <div className={styles.dropdown}>
                                <Dropdown
                                    options={dayHoursList}
                                    value={businessHours[day]?.from || ''}
                                    onChange={(value) => handleBusinessHourChange(day, 'from', value)}
                                    variant={weekDays.includes(day) ? 'primary' : 'secondary'}
                                    disabled={!weekDays.includes(day)}
                                    defaultMessage={t('oset.hours.allDay')}
                                    getOptionLabel={hourOptionLabel}
                                />
                            </div>
                            <div className={styles.dropdown}>
                                <Dropdown
                                    options={dayHoursList}
                                    value={businessHours[day]?.to || ''}
                                    onChange={(value) => handleBusinessHourChange(day, 'to', value)}
                                    variant={weekDays.includes(day) ? 'primary' : 'secondary'}
                                    disabled={!weekDays.includes(day)}
                                    defaultMessage={t('oset.hours.allDay')}
                                    getOptionLabel={hourOptionLabel}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Location Section */}
            <div className={styles.locationSection}>
                <div className={styles.fieldsContainer}>
                    <h2 className={styles.h2}>{t('oset.account.location')}</h2>
                    <InputField
                        label={t('oset.account.country')}
                        value={formData.location.country}
                        onChange={(value) => handleLocationChange('country', value)}
                    />
                    <InputField
                        label={t('oset.account.state')}
                        value={formData.location.state}
                        onChange={(value) => handleLocationChange('state', value)}
                    />
                    <InputField
                        label={t('oset.account.street')}
                        value={formData.location.streetAddress}
                        onChange={(value) => handleLocationChange('streetAddress', value)}
                    />
                    <div className={styles.cityZipRow}>
                        <InputField
                            label={t('oset.account.city')}
                            value={formData.location.city}
                            onChange={(value) => handleLocationChange('city', value)}
                        />
                        <InputField
                            label={t('oset.account.zip')}
                            value={formData.location.zipCode}
                            onChange={(value) => handleLocationChange('zipCode', value)}
                        />
                    </div>
                    <Toggle
                        label={t('oset.account.homeService')}
                        checked={formData.location.homeServicesAvailable}
                        onChange={(value) => handleLocationChange('homeServicesAvailable', value)}
                    />
                </div>
                {/* #1212: Map uses business coordinates from API; defaults to 0,0 if no address set */}
                <div className={styles.mapContainer}>
                    <MapContainer
                        center={[formData.location.latitude || 0, formData.location.longitude || 0]}
                        zoom={formData.location.streetAddress ? 13 : 2}
                        style={{ height: '400px', width: '100%' }}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {/* #1212: Only show marker when address data exists */}
                        {hasAddressData && (
                            <Marker
                                position={[formData.location.latitude, formData.location.longitude]}
                                draggable
                                eventHandlers={{
                                    dragend: (e: any) => {
                                        const { lat, lng } = e.target.getLatLng();
                                        handleLocationChange('latitude', lat);
                                        handleLocationChange('longitude', lng);
                                    }
                                }}
                            />
                        )}
                        {(() => {
                            const MapClickHandler: React.FC = () => {
                                useMapEvents({
                                    click: (ev) => {
                                        handleLocationChange('latitude', ev.latlng.lat);
                                        handleLocationChange('longitude', ev.latlng.lng);
                                    }
                                });
                                return null;
                            };
                            return <MapClickHandler />;
                        })()}
                    </MapContainer>
                </div>
            </div>

            {/* Danger Zone */}
            <div className={styles.dangerZone}>
                <h2 className={styles.h2}>{t('oset.account.dangerZone')}</h2>
                <p className={styles.dangerDescription}>
                    {t('oset.account.dangerDesc')}
                </p>
                <Button
                    label={isDeleting ? t('ost.card.deleting') : t('oset.account.deleteBusiness')}
                    onClick={() => setShowDeleteModal(true)}
                    backgroundColor="var(--destructive-fill)"
                    size="medium"
                    disabled={isDeleting || isLoading || !businessId}
                />
            </div>

            <DeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onDelete={handleDeleteBusiness}
                branchName={t('oset.account.businessAccount')}
                message={t('oset.account.deleteConfirm')}
            />
        </div>
    );
};

export default AccountSettings;